import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/icons';
import { Badge, ErrorState, Field, LoadingRows, Modal, PageHeader, Stat } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatDate, formatDateTime, formatPhone, maskPhone } from '../lib/format';
import { qk, useTestMode } from '../lib/queries';
import type { TestPreviewResult } from '../lib/types';

/** S10 — Test modu: kalıcı banner, sayaçlar, önizleme, son denemeler, TOTP ile canlıya geçiş. */
export default function TestModePage() {
  const testMode = useTestMode();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [newPhone, setNewPhone] = useState('');
  const [previewText, setPreviewText] = useState('Merhaba, talebiniz alındı. Ekibimiz en kısa sürede dönecek.');
  const [previewPhone, setPreviewPhone] = useState('');
  const [previewResult, setPreviewResult] = useState<TestPreviewResult | null>(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [livePhone, setLivePhone] = useState('');
  const [totp, setTotp] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: qk.testMode });
    void queryClient.invalidateQueries({ queryKey: ['audit'] });
  }

  const addNumber = useMutation({
    mutationFn: (phone: string) => api.addAllowlist(phone),
    onSuccess: () => {
      toast.push('Numara izin listesine eklendi.', 'ok');
      setNewPhone('');
      invalidate();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const removeNumber = useMutation({
    mutationFn: (phone: string) => api.removeAllowlist(phone),
    onSuccess: () => {
      toast.push('Numara izin listesinden çıkarıldı.', 'ok');
      invalidate();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const preview = useMutation({
    mutationFn: () => api.testPreview({ text: previewText, to: previewPhone }),
    onSuccess: (result) => setPreviewResult(result),
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const enterLive = useMutation({
    mutationFn: () => api.enterLive({ phone: livePhone, totp }),
    onSuccess: (result) => {
      const warning = result.warnings[0];
      toast.push(warning ? 'Canlı moda geçildi. ' + warning : 'Canlı moda geçildi.', 'ok');
      setLiveOpen(false);
      setTotp('');
      setConfirmed(false);
      invalidate();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const backToTest = useMutation({
    mutationFn: () => api.enterTest(),
    onSuccess: () => {
      toast.push("Test moduna dönüldü. Yalnızca izinli numaralara gönderim yapılır.", 'ok');
      invalidate();
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  if (testMode.isError) return <ErrorState message={errorMessage(testMode.error)} onRetry={() => void testMode.refetch()} />;
  if (testMode.isLoading || !testMode.data) return <LoadingRows rows={8} columns={3} />;

  const state = testMode.data;
  const isTest = state.runtimeMode !== 'LIVE';

  return (
    <div>
      <PageHeader
        title="Test Modu"
        subtitle="Sistem canlı görünür ama yalnızca izinli numaraya yanıt verir. Garanti mimaridir: API reddeder, worker tekrar reddeder."
        actions={
          isTest ? (
            <button type="button" className="btn btn-danger btn-sm" onClick={() => setLiveOpen(true)}>
              <Icon name="warning" size={14} />
              Canlıya geç
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => backToTest.mutate()} disabled={backToTest.isPending}>
              <Icon name="flask" size={14} />
              Test'e dön
            </button>
          )
        }
      />

      <div className="p-6">
        <div className={'mb-4 flex items-center gap-3 rounded-[8px] border px-4 py-3 ' + (isTest ? 'border-[#ead9b4] bg-warn-soft' : 'border-[#cfe3d6] bg-ok-soft')}>
          <Icon name={isTest ? 'flask' : 'warning'} size={18} className={isTest ? 'text-warn' : 'text-ok'} />
          <div className="min-w-0">
            <div className={'text-[13px] font-medium ' + (isTest ? 'text-warn' : 'text-ok')}>
              {isTest ? 'TEST MODU AÇIK' : 'CANLI MOD'}
            </div>
            <div className="text-[12px] text-ink-2">
              {isTest
                ? 'Yalnızca ' + state.allowlist.filter((entry) => entry.active).map((entry) => formatPhone(entry.phone)).join(', ') + ' numarasına gönderim yapılabilir.'
                : 'Tüm numaralara gönderim açık. Limitler ve guard yine çalışır.'}
            </div>
          </div>
          <Badge tone={isTest ? 'warn' : 'ok'} >{state.runtimeMode}</Badge>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 min-[1000px]:grid-cols-4">
          <Stat label="Engellenen (24 sa)" value={state.counters.blocked24h} tone={state.counters.blocked24h > 0 ? 'danger' : undefined} />
          <Stat label="İzinli gönderim (24 sa)" value={state.counters.allowed24h} tone="ok" />
          <Stat label="Bugünkü gönderim" value={state.counters.outboundToday + ' / ' + state.counters.dailyCap} />
          <Stat
            label="Son hata"
            value={state.counters.lastError ? state.counters.lastError.split(' ')[0] : 'yok'}
            title={state.counters.lastError || undefined}
            small={Boolean(state.counters.lastError)}
            tone={state.counters.lastError ? 'warn' : 'ok'}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 min-[1200px]:grid-cols-2">
          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">İzin listesi</h2>
              <span className="mono text-[11px] text-muted">{state.allowlist.length} numara</span>
            </div>
            <div className="panel-body space-y-3">
              <ul className="divide-y divide-line">
                {state.allowlist.map((entry) => (
                  <li key={entry.phone} className="flex items-center gap-2 py-2">
                    <Icon name="phone" size={14} className="text-muted" />
                    <span className="mono text-[13px]">{formatPhone(entry.phone)}</span>
                    {entry.active ? <Badge tone="ok">aktif</Badge> : <Badge>pasif</Badge>}
                    <span className="mono ml-auto text-[11px] text-muted">{formatDate(entry.addedAt)}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm text-danger"
                      aria-label={formatPhone(entry.phone) + ' numarasını çıkar'}
                      onClick={() => removeNumber.mutate(entry.phone)}
                      disabled={state.allowlist.length === 1}
                      title={state.allowlist.length === 1 ? 'Son numara kaldırılamaz' : 'Çıkar'}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </li>
                ))}
              </ul>

              <form
                className="flex items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (newPhone.trim()) addNumber.mutate(newPhone.trim());
                }}
              >
                <Field label="Numara ekle" htmlFor="allowlist-phone" hint="Örnek: +90 551 888 58 86">
                  <input
                    id="allowlist-phone"
                    className="input mono"
                    value={newPhone}
                    onChange={(event) => setNewPhone(event.target.value)}
                    placeholder="+90 5xx xxx xx xx"
                  />
                </Field>
                <button type="submit" className="btn" disabled={!newPhone.trim() || addNumber.isPending}>
                  <Icon name="plus" size={14} />
                  Ekle
                </button>
              </form>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Guard önizlemesi</h2>
              <span className="text-[11px] text-muted">Gerçek gönderim yapılmaz</span>
            </div>
            <div className="panel-body space-y-3">
              <Field label="Hedef numara" htmlFor="preview-phone">
                <input
                  id="preview-phone"
                  className="input mono"
                  value={previewPhone}
                  onChange={(event) => setPreviewPhone(event.target.value)}
                  placeholder="+90 5xx xxx xx xx"
                />
              </Field>
              <Field label="Mesaj" htmlFor="preview-text">
                <textarea
                  id="preview-text"
                  className="textarea min-h-[72px]"
                  value={previewText}
                  onChange={(event) => setPreviewText(event.target.value)}
                />
              </Field>
              <button
                type="button"
                className="btn"
                disabled={!previewPhone.trim() || !previewText.trim() || preview.isPending}
                onClick={() => preview.mutate()}
              >
                <Icon name="shield" size={14} />
                Guard'ı dene
              </button>

              {previewResult ? (
                <div
                  className={'rounded-[4px] border p-3 text-[12px] ' + (previewResult.allowed ? 'border-[#cfe3d6] bg-ok-soft' : 'border-[#eccfcc] bg-danger-soft')}
                  role="status"
                >
                  <div className="flex items-center gap-2">
                    <Badge tone={previewResult.allowed ? 'ok' : 'danger'}>{previewResult.guard}</Badge>
                    <span className="mono">{formatPhone(previewResult.target)}</span>
                  </div>
                  <div className="mt-1 text-ink-2">{previewResult.reason}</div>
                </div>
              ) : (
                <p className="text-[11px] text-muted">Sonuç: ALLOW veya BLOCK + neden. Hiçbir mesaj gönderilmez, kuyruğa yazılmaz.</p>
              )}
            </div>
          </section>
        </div>

        <section className="panel mt-4">
          <div className="panel-head">
            <h2 className="text-[13px]">Son denemeler</h2>
            <span className="mono text-[11px] text-muted">{state.attempts.length} kayıt · numaralar maskeli</span>
          </div>
          <table className="table">
            <caption className="sr-only">Test modu gönderim denemeleri</caption>
            <thead>
              <tr>
                <th scope="col" style={{ width: '160px' }}>Zaman</th>
                <th scope="col" style={{ width: '190px' }}>Hedef</th>
                <th scope="col" style={{ width: '110px' }}>Tür</th>
                <th scope="col" style={{ width: '120px' }}>Karar</th>
                <th scope="col">Neden</th>
              </tr>
            </thead>
            <tbody>
              {state.attempts.map((attempt) => (
                <tr key={attempt.id}>
                  <td className="mono text-[12px] text-muted">{formatDateTime(attempt.at)}</td>
                  <td className="mono text-[12px]">{maskPhone(attempt.to)}</td>
                  <td className="text-[12px]">{attempt.kind === 'image' ? 'medya' : 'metin'}</td>
                  <td>
                    <Badge tone={attempt.decision === 'allowed' ? 'ok' : 'danger'}>{attempt.decision === 'allowed' ? 'izinli' : 'engellendi'}</Badge>
                  </td>
                  <td className="mono text-[12px] text-ink-2">{attempt.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <Modal
        open={liveOpen}
        title="Canlıya geç"
        onClose={() => setLiveOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-sm" onClick={() => setLiveOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={!confirmed || totp.length !== 6 || !livePhone.trim() || enterLive.isPending}
              onClick={() => enterLive.mutate()}
            >
              Canlıya geç
            </button>
          </>
        }
      >
        <div className="space-y-3 text-[13px]">
          <div className="flex gap-2 rounded-[4px] border border-[#eccfcc] bg-danger-soft p-3 text-[12px] text-[#7d1a15]">
            <Icon name="warning" size={16} className="mt-0.5 shrink-0" />
            <div>
              Canlı modda tüm numaralara gönderim açılır. İlk 24 saat günlük limit 20 mesajdır ve her gönderim audit kaydına yazılır.
            </div>
          </div>
          <Field label="Numarayı elle yazın" htmlFor="live-phone" hint="Onay için izinli numaranın tamamı yazılmalı.">
            <input id="live-phone" className="input mono" value={livePhone} onChange={(event) => setLivePhone(event.target.value)} placeholder="+90 551 888 58 86" />
          </Field>
          <Field label="TOTP kodu" htmlFor="live-totp" hint="Authenticator uygulamasındaki 6 haneli kod.">
            <input
              id="live-totp"
              className="input mono tracking-[0.2em]"
              inputMode="numeric"
              maxLength={6}
              value={totp}
              onChange={(event) => setTotp(event.target.value.replace(/[^0-9]/g, ''))}
            />
          </Field>
          <label className="flex items-start gap-2 text-[12px]">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#b4551b]" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
            <span>Riski anladım; test modundan çıkmak istiyorum. Deploy tarafında WA_OUTBOUND_ENABLED=true olmalı.</span>
          </label>
          <p className="text-[11px] leading-4 text-muted">
            Canlıya geçiş hesabınızda TOTP etkin olmasını gerektirir. TOTP kurulu değilse kod doğrulanamaz;
            hesabınızda iki adımlı doğrulamayı etkinleştirdikten sonra tekrar deneyin.
          </p>
        </div>
      </Modal>
    </div>
  );
}
